<!--
  DropdownCifra — Dropdown con posicionamiento mediante Popper que evita
  clipping por contenedores con overflow.

  Props:
    disabled   (Boolean, default: false): deshabilita apertura/cierre
    direccion  (String, default: 'abajo'): 'abajo' | 'arriba'
    alineacion (String, default: 'izquierda'): 'izquierda' | 'derecha'

  Slots:
    #toggle="{ toggle }" — contenido del trigger
    #menu="{ close }" — contenido del menú (<li> items)
-->
<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import { createPopper } from '@popperjs/core';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  direccion: { type: String, default: 'abajo' },
  alineacion: { type: String, default: 'izquierda' },
});

const emit = defineEmits(['open', 'close']);

const open = ref(false);
const toggleRef = ref(null);
const menuRef = ref(null);
const popperInstance = ref(null);

function getPlacement() {
  const dir = props.direccion === 'arriba' ? 'top' : 'bottom';
  const ali = props.alineacion === 'derecha' ? 'end' : 'start';
  return `${dir}-${ali}`;
}

function destruirPopper() {
  if (popperInstance.value) {
    popperInstance.value.destroy();
    popperInstance.value = null;
  }
}

function close() {
  open.value = false;
  destruirPopper();
  emit('close');
}

function abrir() {
  open.value = true;
  nextTick(() => {
    if (toggleRef.value && menuRef.value) {
      popperInstance.value = createPopper(toggleRef.value, menuRef.value, {
        placement: getPlacement(),
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 2] },
          },
          {
            name: 'preventOverflow',
            options: { padding: 4 },
          },
        ],
      });
    }
  });
  emit('open');
}

function toggle() {
  if (props.disabled) return;
  if (open.value) {
    close();
  } else {
    abrir();
  }
}

function onClickOutside(event) {
  if (!open.value) return;
  if (toggleRef.value?.contains(event.target)) return;
  if (menuRef.value?.contains(event.target)) return;
  close();
}

function onKeydown(event) {
  if (event.key === 'Escape' && open.value) close();
}

function onScroll(event) {
  if (!open.value) return;
  if (toggleRef.value?.contains(event.target)) return;
  if (menuRef.value?.contains(event.target)) return;
  close();
}

function onResize() {
  if (open.value) close();
}

onMounted(() => {
  document.addEventListener('click', onClickOutside, true);
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true);
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('scroll', onScroll, { capture: true, passive: true });
  window.removeEventListener('resize', onResize);
  destruirPopper();
});
</script>

<template>
  <span ref="toggleRef" class="dropdown-cifra">
    <slot name="toggle" :open="open" :toggle="toggle" />
    <ul
      v-if="open"
      ref="menuRef"
      class="dropdown-menu"
      style="display:block"
    >
      <slot name="menu" :close="close" />
    </ul>
  </span>
</template>
